import React, { useEffect, useRef } from 'react'
import { t } from '../i18n'
import { dayLabel, localDay } from '../Utils/time'
import AdSlot from './AdSlot'
import { Spinner } from './Icons'
import StoryCard from './StoryCard'

/* Placeholder cards while a feed loads (spec 6: skeletons, not spinners). */
export function SkeletonCards({ count = 4, main = false }) {
  return (
    <div aria-busy="true" aria-label={t('common.loading')}>
      {main && (
        <div className="feed-item skeleton-card">
          <div className="sk sk-line sk-meta" />
          <div className="sk sk-line sk-big" />
          <div className="sk sk-line sk-big sk-short" />
          <div className="sk sk-box" />
        </div>
      )}
      {Array.from({ length: count }).map((_, i) => (
        <div className="feed-item skeleton-card" key={i}>
          <div className="sk sk-line sk-meta" />
          <div className="sk sk-line" />
          <div className="sk sk-line sk-short" />
          <div className="sk sk-line sk-footer" />
        </div>
      ))}
    </div>
  )
}

export function FeedError({ onRetry }) {
  return (
    <div className="feed-message" role="alert">
      <p>{t('feed.error')}</p>
      <button type="button" className="btn btn-secondary" onClick={onRetry}>
        {t('common.retry')}
      </button>
    </div>
  )
}

/* Loads the next page when the end of the list comes near. */
function LoadMore({ feed }) {
  const ref = useRef(null)
  const { loadMore, nextCursor, loadingMore, moreError } = feed
  useEffect(() => {
    if (!nextCursor || !ref.current || moreError) return undefined
    const io = new IntersectionObserver(([entry]) => entry.isIntersecting && loadMore(), {
      rootMargin: '800px 0px',
    })
    io.observe(ref.current)
    return () => io.disconnect()
  }, [nextCursor, loadMore, moreError, feed.items.length])

  if (!nextCursor) return null
  return (
    <div ref={ref} className="load-more">
      {moreError ? (
        <button type="button" className="btn btn-secondary" onClick={loadMore}>
          {t('common.retry')}
        </button>
      ) : (
        loadingMore && <Spinner size={22} />
      )}
    </div>
  )
}

/* Ads go after the 4th regular card, then after every 6th (spec 5.12). Stories
   brought in by the pill don't count, and an ad is never the last item before
   the next page loads. */
function adAfter(regularCount) {
  return regularCount === 4 || (regularCount > 4 && (regularCount - 4) % 6 === 0)
}

export default function FeedList({
  feed,
  ads = true,
  from = 'feed',
  empty = null,
  skeletonMain = false,
  hideTopic = null,
  daySeparators = false,
  tagFor = null,
}) {
  if (feed.status === 'loading') return <SkeletonCards count={5} main={skeletonMain} />
  if (feed.status === 'error') return <FeedError onRetry={feed.retry} />
  if (feed.items.length === 0) {
    return feed.mainStory ? null : (
      <div className="feed-message">{empty || <p>{t('feed.empty')}</p>}</div>
    )
  }

  const rows = []
  let regular = 0
  let day = null
  feed.items.forEach((story, i) => {
    const isNew = feed.newIds.has(story.id)
    // Newest-first topic pages (articles): "Danas", "Jučer", "Ponedjeljak, 12. 10." (spec 9.3a).
    if (daySeparators && localDay(story.publishedAt) !== day) {
      day = localDay(story.publishedAt)
      rows.push(
        <h2 className="day-separator" key={`day-${day}`}>
          {dayLabel(story.publishedAt)}
        </h2>
      )
    }
    rows.push(
      <div className="feed-item" key={story.id}>
        <StoryCard
          story={story}
          // isFresh: new since the push that opened this topic (spec 9.12).
          isNew={isNew || Boolean(story.isFresh)}
          from={from}
          hideTopic={hideTopic}
          tag={tagFor ? tagFor(story) : undefined}
        />
      </div>
    )
    if (isNew) return
    regular += 1
    const isLast = i === feed.items.length - 1
    if (ads && !isLast && adAfter(regular))
      rows.push(<AdSlot key={`ad-${regular}`} position={regular} />)
  })

  const days = feed.extra?.windowDays
  return (
    <>
      {rows}
      <LoadMore feed={feed} />
      {/* The end of what we show, instead of a load that finds nothing. */}
      {!feed.nextCursor && days > 0 && (
        <p className="feed-end">{t('feed.endOfList', { count: days })}</p>
      )}
    </>
  )
}
